import React from 'react';
import JurorCard from './JurorCard/JurorCard';
import {Paper, Tooltip} from '@mui/material';
import Typography from '@mui/material/Typography';
import {useSelector} from 'react-redux';
import useStyles from './Case.styles';
import { useCase, useJurors, useJurorsForRow } from '../../db/hooks';
import {
    emptyJuryBox, emptyAlternates, fillEmptySpaces, fillFromPool, updateJuror,
    resetCustomSeatNumbers
} from '../../db/actions';
import {getAll} from '../../state/multiSelect/selectors';
import PublishIcon from '@mui/icons-material/Publish';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from "@mui/icons-material/LastPage";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import IconButton from '@mui/material/IconButton';
import jsonifyCompare from '../../util/jsonifyCompare';
import Spacer from './Spacer';
import {getMode} from "../../state/ethereal/selectors";
import EmptyJuryBoxSeat from "./JurorCard/EmptyJuryBoxSeat";
import ResetSeatNumbersDialog from './ResetSeatNumbersDialog';
import { useAlert } from '../../util/alertDialogContext';
import { getSeatLayout } from '../../util/seatLayout';
import {
    DemographicsFilterSelect,
    FilterBar,
    FilterSearchField,
    StatusFilterChips,
    jurorMatchesDemographics,
    jurorMatchesStatuses,
    jurorMatchesText,
    useDebouncedValue,
    useSharedJurorFilters,
} from './JurorFilterControls';

const EMPTY_SET = new Set();
const EMPTY_CUSTOM_SEATS = {};

const FormRow = ({
                     numCols, rowNum, juryBoxSize, mode, caseId, civilCase, demographicCategories, showDemographics,
                     addEmptyJuror, getEmptyJurors, reverseRow, handleUpdateJuror, multiSelect, spacers,
                     searchActive, searchMatches, customSeatNumbers, conflictSeats
                 }) => {

    const classes = useStyles();

    // Use Dexie hook for row jurors
    const { rowJurors } = useJurorsForRow(caseId, rowNum, numCols, mode === 'jury-box' ? spacers : [], mode, customSeatNumbers, juryBoxSize);
    const jurorsForRow = rowJurors;

    const items = [];
    for (let jurorInRow of jurorsForRow) {
        // Filler cells past the box size compare by default (positional)
        // number — custom seat numbers may legitimately exceed the box size.
        if (jurorInRow.defaultSeatNumber > juryBoxSize) {
            items.push(<div style={{flex: 1}} key={`empty-juror-${jurorInRow.spaceNumber}`}>&nbsp;</div>);
            continue;
        }

        if (jurorInRow.isSpacer) {
            items.push(<Spacer key={`space-${jurorInRow.spaceNumber}`}
                               caseId={caseId}
                               spaceNumber={jurorInRow.spaceNumber}/>);
        } else if (!jurorInRow.jurorInfo) {
            items.push(<EmptyJuryBoxSeat caseId={caseId}
                                         key={'empty-juror' + jurorInRow.spaceNumber}
                                         spaceNumber={jurorInRow.spaceNumber}
                                         seatNumber={jurorInRow.actualSeatNumber}
                                         defaultSeatNumber={jurorInRow.defaultSeatNumber}
                                         isCustomSeatNumber={jurorInRow.isCustomSeatNumber}
                                         isConflictSeatNumber={conflictSeats.has(jurorInRow.actualSeatNumber)}/>);
        } else {
            items.push(
                <JurorCard
                    type="juror"
                    jurorId={jurorInRow.jurorInfo ? jurorInRow.jurorInfo._id : null}
                    juror={jurorInRow.jurorInfo}
                    seatNumber={jurorInRow.actualSeatNumber}
                    spaceNumber={jurorInRow.spaceNumber}
                    defaultSeatNumber={jurorInRow.defaultSeatNumber}
                    isCustomSeatNumber={jurorInRow.isCustomSeatNumber}
                    isConflictSeatNumber={conflictSeats.has(jurorInRow.actualSeatNumber)}
                    key={`space-${jurorInRow.spaceNumber}`}
                    multiSelect={multiSelect}
                    caseId={caseId}
                    mode={mode}
                    civilCase={civilCase}
                    demographicCategories={demographicCategories}
                    showDemographics={showDemographics}
                    addEmptyJuror={addEmptyJuror}
                    getEmptyJurors={getEmptyJurors}
                    updateJurorInParent={handleUpdateJuror}
                    searchActive={searchActive}
                    isSearchMatch={searchActive && searchMatches.has(jurorInRow.jurorInfo._id)}
                />
            );
        }
    }
    return (
        <React.Fragment>
            <div className={classes.formRow} style={{flexDirection: reverseRow ? 'row-reverse' : 'row'}}>
                {items}
            </div>
        </React.Fragment>
    );
};

const FormGrid = ({
                      numRows,
                      numCols,
                      mode,
                      caseId,
                      juryBoxSize,
                      civilCase,
                      demographicCategories,
                      showDemographics,
                      addEmptyJuror,
                      getEmptyJurors,
                      reverseRow,
                      multiSelect,
                      handleUpdateJuror,
                      seatNumberFlow,
                      spacers,
                      searchActive,
                      searchMatches,
                      customSeatNumbers,
                      conflictSeats
                  }) => {
    const rows = [];
    for (let i = 0; i < numRows; i++) {
        let tmpReverseRow = reverseRow;
        if (seatNumberFlow === "snake" && i % 2 > 0) {
            tmpReverseRow = !reverseRow;
        }
        rows.push(
            <FormRow key={i}
                     searchActive={searchActive}
                     searchMatches={searchMatches}
                     customSeatNumbers={customSeatNumbers}
                     conflictSeats={conflictSeats}
                     rowNum={i}
                     caseId={caseId}
                     mode={mode}
                     juryBoxSize={juryBoxSize}
                     civilCase={civilCase}
                     demographicCategories={demographicCategories}
                     showDemographics={showDemographics}
                     addEmptyJuror={addEmptyJuror}
                     getEmptyJurors={getEmptyJurors}
                     reverseRow={tmpReverseRow}
                     handleUpdateJuror={handleUpdateJuror}
                     multiSelect={multiSelect}
                     spacers={spacers}
                     numCols={numCols}/>,
        );
    }
    return <React.Fragment>{rows}</React.Fragment>;
};

const JuryBox = ({
                     caseId,
                     civilCase,
                     demographicCategories,
                     showDemographics,
                     jurorsPerRow,
                     seatNumberStartPosition,
                     seatNumberFlow
                 }) => {

    //console.log("Rendering jurybox...");

    const classes = useStyles();

    // Use Dexie hook for case info
    const { caseData } = useCase(caseId);
    const { jurors } = useJurors(caseId);
    const juryBoxSize = parseInt(caseData?.juryBoxSize || 12);
    const spacers = caseData?.spacers || [];
    const numberOfAlternates = caseData?.numberOfAlternates || 0;
    const mode = useSelector(getMode);
    const multiSelect = useSelector(getAll, jsonifyCompare('multi-select[jurybox]'));
    const { alert } = useAlert();
    const [resetDialogOpen, setResetDialogOpen] = React.useState(false);

    const numCols = jurorsPerRow;
    let numRows = Math.ceil((juryBoxSize + spacers.length) / jurorsPerRow);
    const reverseRow = seatNumberStartPosition.indexOf('Right') >= 0;
    const reverseColumn = seatNumberStartPosition.indexOf('bottom') >= 0;
    // useRef so registered refs survive re-renders, and useCallback so these
    // props stay referentially stable — JurorCard is memoized and new
    // function references on every render would defeat that.
    const emptyJurorsRef = React.useRef({});
    let prompt = '';

    if (mode === "alternates") {
        numRows = Math.ceil(numberOfAlternates / jurorsPerRow);
    }

    const handleUpdateJuror = React.useCallback(async (juror) => {
        await updateJuror(caseId, juror._id, juror);
    }, [caseId]);

    const addEmptyJuror = React.useCallback((seatNumber, jurorRef) => {
        emptyJurorsRef.current[seatNumber] = jurorRef;
    }, []);

    const getEmptyJurors = React.useCallback(() => {
        return emptyJurorsRef.current;
    }, []);

    // Search and filters highlight matching cards and dim the rest —
    // nothing is hidden, so seat positions stay stable. Search and
    // demographics are shared with the dismissed area and jury pool;
    // the intent filter is jury-box specific.
    const {
        search: searchInput,
        setSearch: setSearchInput,
        demographics: demographicFilter,
        setDemographics: setDemographicFilter,
    } = useSharedJurorFilters();
    const [statusFilter, setStatusFilter] = React.useState([]);
    const searchTerm = useDebouncedValue(searchInput.trim(), 150);
    const searchActive =
        searchTerm.length > 0 ||
        demographicFilter.length > 0 ||
        statusFilter.length > 0;

    // Seat numbers used by more than one space (transient state while the
    // user relabels seats) — highlighted so conflicts are easy to resolve.
    const conflictSeats = React.useMemo(() => {
        const counts = new Map();
        for (const seat of getSeatLayout(caseData, mode)) {
            counts.set(seat.seatNumber, (counts.get(seat.seatNumber) || 0) + 1);
        }
        const conflicts = new Set();
        for (const [num, count] of counts) {
            if (count > 1) conflicts.add(num);
        }
        return conflicts;
    }, [caseData, mode]);

    const seatField = mode === 'jury-box' ? 'seatNumber' : 'alternateSeatNumber';
    const discardedField = mode === 'jury-box' ? 'discarded' : 'alternateDiscarded';
    const seatedJurors = React.useMemo(
        () => jurors.filter(j => j[seatField] != null && j[seatField] > 0 && !j[discardedField]),
        [jurors, seatField, discardedField],
    );

    const searchMatches = React.useMemo(() => {
        if (!searchActive) {
            return EMPTY_SET;
        }
        const matches = new Set();
        for (const juror of seatedJurors) {
            if (
                jurorMatchesText(juror, searchTerm) &&
                jurorMatchesDemographics(juror, demographicFilter) &&
                jurorMatchesStatuses(juror, statusFilter)
            ) {
                matches.add(juror._id);
            }
        }
        return matches;
    }, [seatedJurors, searchTerm, demographicFilter, statusFilter, searchActive]);

    // The first-use hint shows only when nobody is seated in the current area.
    // seatedJurors is already the mode-aware set of occupants (positive seat
    // number, not discarded), so it counts custom-numbered seats that may
    // exceed the box/alternate size — a raw `<= size` check missed those and
    // left the hint up even with jurors clearly seated.
    const isSeatingEmpty = seatedJurors.length === 0;

    if (isSeatingEmpty) {
        prompt = 'Click an empty juror card to add info';
    }

    let label = mode === 'jury-box' ? 'Jury Box' : "Alternates";

    return (
        <Paper elevation={0} className={classes.container}>
            <div style={{display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 8}}>
                <Typography
                    className={classes.boxTitle}
                    variant="h6"
                    style={{paddingBottom: 0, whiteSpace: 'nowrap'}}
                >
                    <b>{label}</b>
                </Typography>
                <FilterBar style={{flexWrap: 'nowrap'}}>
                    <FilterSearchField
                        value={searchInput}
                        onChange={setSearchInput}
                        placeholder={`Search ${label.toLowerCase()}…`}
                        width={180}
                    />
                    <DemographicsFilterSelect
                        jurors={seatedJurors}
                        value={demographicFilter}
                        onChange={setDemographicFilter}
                        placeholder="Demographics"
                        minWidth={170}
                    />
                    <StatusFilterChips value={statusFilter} onChange={setStatusFilter} />
                </FilterBar>
                {!!prompt &&
                    <Typography variant="body2" style={{whiteSpace: 'nowrap', flex: 1, textAlign: 'center'}}>
                        <b>{prompt}</b>
                    </Typography>
                }
                <div style={{display: "flex", alignItems: "center", justifyContent: "flex-end", flex: 1}} className={classes.boxButtons}>
                    {Object.keys(caseData?.customSeatNumbers?.[mode] || EMPTY_CUSTOM_SEATS).length > 0 &&
                        <Tooltip title={"Reset all seat numbers to defaults"}>
                            <IconButton
                                onClick={() => setResetDialogOpen(true)}
                                size="large">
                                <RestartAltIcon/>
                            </IconButton>
                        </Tooltip>
                    }
                    <Tooltip title={`Clear ${label} / Send all to jury pool`}>
                        <IconButton
                            onClick={() => {
                                alert({
                                    title: `Empty the ${label}?`,
                                    msg: `Everyone in the ${label.toLowerCase()} will be sent back to the jury pool.`,
                                    variant: 'danger',
                                    onSubmit: async () => {
                                        if (mode === 'jury-box') {
                                            await emptyJuryBox(caseId);
                                        } else {
                                            await emptyAlternates(caseId);
                                        }
                                    },
                                });
                            }}
                            size="large">
                            <SystemUpdateAltIcon/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={"Slide jurors over to fill empty seats"}>
                        <IconButton
                            onClick={async () => {
                                await fillEmptySpaces(caseId, mode);
                            }}
                            size="large">
                            {reverseRow ? <LastPageIcon/> : <FirstPageIcon/>}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={"Fill from jury pool"}>
                        <IconButton
                            onClick={async () => {
                                await fillFromPool(caseId, mode);
                            }}
                            size="large">
                            <PublishIcon/>
                        </IconButton>
                    </Tooltip>
                </div>
            </div>
            <div id="jury-box-grid-container"
                style={{
                minHeight: (numRows * 143),
                display: 'flex',
                flexDirection: reverseColumn ? 'column-reverse' : 'column',
                overflowX: 'auto'
            }}>
                <FormGrid numCols={numCols}
                          numRows={numRows}
                          searchActive={searchActive}
                          searchMatches={searchMatches}
                          customSeatNumbers={caseData?.customSeatNumbers?.[mode] || EMPTY_CUSTOM_SEATS}
                          conflictSeats={conflictSeats}
                          mode={mode}
                          caseId={caseId}
                          civilCase={civilCase}
                          demographicCategories={demographicCategories}
                          showDemographics={showDemographics}
                          multiSelect={multiSelect}
                          handleUpdateJuror={handleUpdateJuror}
                          reverseRow={reverseRow}
                          seatNumberFlow={seatNumberFlow}
                          getEmptyJurors={getEmptyJurors}
                          addEmptyJuror={addEmptyJuror}
                          spacers={spacers}
                          juryBoxSize={mode === "jury-box" ? juryBoxSize : numberOfAlternates}/>
            </div>
            <ResetSeatNumbersDialog
                open={resetDialogOpen}
                label={label}
                onClose={() => setResetDialogOpen(false)}
                onConfirm={async ({ keepJurorsSeated }) => {
                    setResetDialogOpen(false);
                    await resetCustomSeatNumbers(caseId, mode, { keepJurorsSeated });
                }}
            />
        </Paper>
    );
};

export default JuryBox;
